/* Curved product slider: cards drift right to left along the inside of a cylinder, draggable with momentum. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
  const PITCH = 196;
  const RADIUS = 16;

  /* Design width per screen size: the curve is drawn in these units and scaled to fit. */
  const designFor = (width) => (width <= 600 ? 640 : width <= 1024 ? 1040 : 1440);

  const roundedQuad = (pts, r) => {
    const n = pts.length;
    let d = '';
    for (let i = 0; i < n; i++) {
      const p = pts[i];
      const prev = pts[(i - 1 + n) % n];
      const next = pts[(i + 1) % n];
      const toPrev = Math.hypot(prev[0] - p[0], prev[1] - p[1]) || 1;
      const toNext = Math.hypot(next[0] - p[0], next[1] - p[1]) || 1;
      const a = [p[0] + ((prev[0] - p[0]) / toPrev) * r, p[1] + ((prev[1] - p[1]) / toPrev) * r];
      const b = [p[0] + ((next[0] - p[0]) / toNext) * r, p[1] + ((next[1] - p[1]) / toNext) * r];
      d += `${i === 0 ? 'M' : 'L'}${a[0].toFixed(1)} ${a[1].toFixed(1)}Q${p[0].toFixed(1)} ${p[1].toFixed(1)} ${b[0].toFixed(1)} ${b[1].toFixed(1)}`;
    }
    return d + 'Z';
  };

  const init = (root) => {
    if (root.dataset.skCurveReady) return;
    const stage = root.querySelector('[data-sk-curve-stage]');
    const originals = [...root.querySelectorAll('[data-sk-curve-card]')];
    if (!stage || !originals.length) return;
    root.dataset.skCurveReady = 'true';

    /* Fill the ring with at least ten cards so it never shows a gap. */
    const cards = [...originals];
    let k = 0;
    while (cards.length < 10) {
      const clone = originals[k % originals.length].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      stage.appendChild(clone);
      cards.push(clone);
      k++;
    }
    const media = cards.map((card) => card.querySelector('.sk-curve-media'));
    const count = cards.length;
    const loop = count * PITCH;
    const speed = Number(root.dataset.speed || 50);

    let offset = 0;
    let velocity = 0;
    let target = null;
    let dragging = false;
    let hovering = false;
    let moved = 0;
    let lastX = 0;
    let lastT = 0;
    let visible = false;
    let running = false;
    let last = performance.now();
    let design = 1440;
    let u = 1;

    const measure = () => {
      const width = stage.clientWidth;
      design = designFor(window.innerWidth);
      u = width / design;
      /* Tallest edge card is about 372 units; leave room for the lift and the floor shadow. */
      stage.style.height = `${Math.round(430 * u)}px`;
    };

    const layout = () => {
      const cx = design / 2;
      const half = design / 2 - 14;
      const cy0 = 430 / 2 + 4;

      for (let i = 0; i < count; i++) {
        let t = (((i * PITCH - offset) % loop) + loop) % loop;
        t -= loop / 2;
        const x = cx + t * (1 + 0.083 * Math.pow(t / half, 2));
        const d = Math.abs(x - cx) / half;
        const w = 170 + 48 * d * d;
        const xl = x - w / 2;
        const xr = x + w / 2;
        const card = cards[i];
        if (xr < -60 || xl > design + 60) {
          card.style.visibility = 'hidden';
          continue;
        }
        const ql = Math.abs(xl - cx) / half;
        const qr = Math.abs(xr - cx) / half;
        const hl = 280 + 92 * Math.pow(ql, 2.6);
        const hr = 280 + 92 * Math.pow(qr, 2.6);
        const cy = cy0 - 12 * d * d;
        const boxH = Math.max(hl, hr);
        const top = cy - boxH / 2;
        const wpx = w * u;
        const hpx = boxH * u;
        card.style.visibility = '';
        card.style.transform = `translate3d(${(xl * u).toFixed(2)}px, ${(top * u).toFixed(2)}px, 0)`;
        card.style.width = `${wpx.toFixed(2)}px`;
        card.style.height = `${hpx.toFixed(2)}px`;
        card.style.clipPath = `path("${roundedQuad([
          [0, (cy - hl / 2 - top) * u],
          [wpx, (cy - hr / 2 - top) * u],
          [wpx, (cy + hr / 2 - top) * u],
          [0, (cy + hl / 2 - top) * u]
        ], RADIUS * u)}")`;
        /* Light falls off toward the edges of the curve; the far side of each card is darker. */
        card.style.setProperty('--d', d.toFixed(3));
        card.style.setProperty('--side', t < 0 ? '1' : '-1');
        card.style.zIndex = String(100 - Math.round(d * 50));
        /* Photos slide slightly inside their frame for depth. */
        if (media[i]) media[i].style.transform = `translate3d(${(-t / half * 7).toFixed(2)}%, 0, 0) scale(1.16)`;
      }
    };

    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!dragging) {
        if (target !== null) {
          const diff = target - offset;
          offset += reduce.matches ? diff : diff * Math.min(1, dt * 9);
          if (Math.abs(diff) < 0.5) { offset = target; target = null; }
        } else {
          const paused = reduce.matches || (hovering && hoverable.matches);
          offset += ((paused ? 0 : speed) + velocity) * dt;
          velocity *= Math.pow(0.05, dt);
          if (Math.abs(velocity) < 0.5) velocity = 0;
        }
      }
      layout();
      if (visible) requestAnimationFrame(frame); else running = false;
    };
    const start = () => {
      if (running) return;
      running = true;
      last = performance.now();
      requestAnimationFrame(frame);
    };

    /* Dragging */
    stage.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      dragging = true;
      moved = 0;
      velocity = 0;
      target = null;
      lastX = event.clientX;
      lastT = performance.now();
      stage.setPointerCapture(event.pointerId);
      stage.classList.add('is-dragging');
      start();
    });
    stage.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const now = performance.now();
      const dx = (event.clientX - lastX) / u;
      const dt = Math.max(1, now - lastT) / 1000;
      offset -= dx;
      moved += Math.abs(event.clientX - lastX);
      velocity = 0.8 * (-dx / dt) + 0.2 * velocity;
      lastX = event.clientX;
      lastT = now;
    });
    const release = (event) => {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-dragging');
      if (performance.now() - lastT > 80) velocity = 0;
      velocity = Math.max(-2400, Math.min(2400, velocity));
      try { stage.releasePointerCapture(event.pointerId); } catch (e) {}
    };
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
    stage.addEventListener('click', (event) => {
      if (moved > 6) { event.preventDefault(); event.stopPropagation(); }
    }, true);
    stage.addEventListener('pointerenter', () => { hovering = true; });
    stage.addEventListener('pointerleave', () => { hovering = false; });

    /* Arrow buttons and keyboard focus glide one card at a time. */
    const snapTo = (value) => { velocity = 0; target = value; start(); };
    const nearest = () => Math.round((offset - loop / 2) / PITCH) * PITCH + loop / 2;
    root.querySelector('[data-sk-curve-prev]')?.addEventListener('click', () => snapTo((target ?? nearest()) - PITCH));
    root.querySelector('[data-sk-curve-next]')?.addEventListener('click', () => snapTo((target ?? nearest()) + PITCH));
    cards.forEach((card, i) => card.addEventListener('focus', () => {
      const base = i * PITCH - loop / 2;
      const turns = Math.round((offset - base) / loop);
      snapTo(base + turns * loop);
    }));

    const observer = new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
      if (visible) { root.classList.add('is-in'); start(); }
    }, { rootMargin: '120px' });
    observer.observe(root);
    window.addEventListener('resize', () => { measure(); layout(); }, { passive: true });
    measure();
    layout();
    root.classList.add('is-ready');
  };

  const boot = () => document.querySelectorAll('[data-sk-curve]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
